import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import {
  getPublicAssessmentLink,
  getAssessmentQuestions,
  startAssessment,
  submitAssessment,
} from '../utils/api';
import { ChevronLeft, ChevronRight, CheckCircle, AlertCircle } from 'lucide-react';

const Assessment = () => {
  const { linkUuid } = useParams();
  const navigate = useNavigate();

  const [linkInfo, setLinkInfo] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentStep, setCurrentStep] = useState('intro'); // intro, info, questions, submitting
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [userInfo, setUserInfo] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });
  const [assessmentUuid, setAssessmentUuid] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLinkInfo();
    loadQuestions();
  }, [linkUuid]);

  const loadLinkInfo = async () => {
    try {
      const response = await getPublicAssessmentLink(linkUuid);
      setLinkInfo(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Assessment link not found or expired');
    } finally {
      setLoading(false);
    }
  };

  const loadQuestions = async () => {
    try {
      const response = await getAssessmentQuestions();
      setQuestions(response.data);
      setAnswers(response.data.map(() => ({ most: null, least: null })));
    } catch (err) {
      console.error('Error loading questions:', err);
    }
  };

  const handleStartAssessment = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const response = await startAssessment({
        link_uuid: linkUuid,
        ...userInfo,
      });
      setAssessmentUuid(response.data.assessment_uuid);
      setCurrentStep('questions');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start assessment');
    }
  };

  const handleSelectOption = (type, style) => {
    const newAnswers = [...answers];
    const current = newAnswers[currentQuestion];

    // Can't select the same option for both most and least
    if (type === 'most' && current.least === style) {
      return;
    }
    if (type === 'least' && current.most === style) {
      return;
    }

    newAnswers[currentQuestion] = {
      ...current,
      [type]: style,
    };
    setAnswers(newAnswers);
  };

  const isQuestionComplete = () => {
    const current = answers[currentQuestion];
    return current?.most && current?.least;
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    setCurrentStep('submitting');
    setError(null);

    try {
      const response = await submitAssessment(assessmentUuid, answers);
      navigate(`/results/${assessmentUuid}`, {
        state: { results: response.data.results },
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit assessment');
      setCurrentStep('questions');
    }
  };

  const completedCount = answers.filter((a) => a.most && a.least).length;
  const progress = (completedCount / questions.length) * 100;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tsvmap-orange" />
      </div>
    );
  }

  if (error && currentStep === 'intro') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Assessment Unavailable</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header style={{ backgroundColor: '#0B2134' }} className="shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Logo size="small" />
          {currentStep === 'questions' && (
            <div className="text-sm text-white">
              Question {currentQuestion + 1} of {questions.length}
            </div>
          )}
        </div>
        {currentStep === 'questions' && (
          <div className="h-1 bg-gray-200">
            <div
              className="h-full bg-tsvmap-orange transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Intro Step */}
        {currentStep === 'intro' && linkInfo && (
          <div className="bg-white rounded-lg shadow-lg p-8 animate-fade-in">
            <h1 className="text-2xl font-bold text-tsvmap-blue mb-4">
              {linkInfo.title || 'DISC Personality Assessment'}
            </h1>
            {linkInfo.organization_name && (
              <p className="text-gray-600 mb-2">
                Provided by: <span className="font-semibold">{linkInfo.organization_name}</span>
              </p>
            )}
            {linkInfo.job_role_title && (
              <p className="text-gray-600 mb-4">
                Position: <span className="font-semibold">{linkInfo.job_role_title}</span>
              </p>
            )}
            {linkInfo.description && (
              <p className="text-gray-700 mb-6">{linkInfo.description}</p>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">About This Assessment</h3>
              <p className="text-blue-800 text-sm">
                The DISC assessment measures four dimensions of behavior: Dominance, Influence,
                Steadiness, and Conscientiousness. This helps identify your natural behavioral
                style and how you interact with others.
              </p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-gray-900 mb-2">Instructions</h3>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• There are {questions.length} questions in this assessment</li>
                <li>• For each question, select which word describes you MOST and which describes you LEAST</li>
                <li>• Answer based on your natural tendencies, not how you think you should answer</li>
                <li>• There are no right or wrong answers</li>
                <li>• The assessment takes approximately 10-15 minutes</li>
              </ul>
            </div>

            <button
              onClick={() => setCurrentStep('info')}
              className="w-full bg-tsvmap-orange text-white py-3 px-6 rounded-lg font-semibold hover:bg-tsvmap-orange-dark transition-colors"
            >
              Get Started
            </button>
          </div>
        )}

        {/* User Info Step */}
        {currentStep === 'info' && (
          <div className="bg-white rounded-lg shadow-lg p-8 animate-fade-in">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Your Information</h2>
            <form onSubmit={handleStartAssessment} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={userInfo.first_name}
                    onChange={(e) =>
                      setUserInfo({ ...userInfo, first_name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tsvmap-orange focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={userInfo.last_name}
                    onChange={(e) =>
                      setUserInfo({ ...userInfo, last_name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tsvmap-orange focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={userInfo.email}
                  onChange={(e) =>
                    setUserInfo({ ...userInfo, email: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tsvmap-orange focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone (optional)
                </label>
                <input
                  type="tel"
                  value={userInfo.phone}
                  onChange={(e) =>
                    setUserInfo({ ...userInfo, phone: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tsvmap-orange focus:border-transparent"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setCurrentStep('intro')}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-tsvmap-orange text-white py-3 px-6 rounded-lg font-semibold hover:bg-tsvmap-orange-dark transition-colors"
                >
                  Begin Assessment
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Questions Step */}
        {currentStep === 'questions' && questions[currentQuestion] && (
          <div className="bg-white rounded-lg shadow-lg p-8 animate-fade-in">
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Select the word that describes you MOST and the word that describes you LEAST
              </h2>
              <p className="text-sm text-gray-500">
                Choose one word for MOST and a different word for LEAST
              </p>
            </div>

            <div className="space-y-4 mb-8">
              {['D', 'I', 'S', 'C'].map((style) => {
                const question = questions[currentQuestion];
                const option = question[`option_${style.toLowerCase()}`];
                const current = answers[currentQuestion];
                const isMost = current?.most === style;
                const isLeast = current?.least === style;

                return (
                  <div
                    key={style}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      isMost
                        ? 'border-green-500 bg-green-50'
                        : isLeast
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-medium text-gray-900">{option}</span>
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleSelectOption('most', style)}
                          disabled={isLeast}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            isMost
                              ? 'bg-green-500 text-white'
                              : isLeast
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-gray-100 text-gray-700 hover:bg-green-100 hover:text-green-700'
                          }`}
                        >
                          MOST
                        </button>
                        <button
                          onClick={() => handleSelectOption('least', style)}
                          disabled={isMost}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            isLeast
                              ? 'bg-red-500 text-white'
                              : isMost
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-gray-100 text-gray-700 hover:bg-red-100 hover:text-red-700'
                          }`}
                        >
                          LEAST
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                {error}
              </div>
            )}

            <div className="flex items-center justify-between">
              <button
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
                className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-5 w-5 mr-1" />
                Previous
              </button>

              <div className="flex items-center gap-2">
                {questions.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentQuestion(idx)}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      idx === currentQuestion
                        ? 'bg-tsvmap-orange'
                        : answers[idx]?.most && answers[idx]?.least
                        ? 'bg-green-500'
                        : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>

              {currentQuestion === questions.length - 1 ? (
                <button
                  onClick={handleSubmit}
                  disabled={completedCount < questions.length}
                  className="flex items-center px-6 py-2 bg-tsvmap-orange text-white rounded-lg font-semibold hover:bg-tsvmap-orange-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Submit
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  disabled={!isQuestionComplete()}
                  className="flex items-center px-4 py-2 text-tsvmap-orange hover:text-tsvmap-orange-dark disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="h-5 w-5 ml-1" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Submitting Step */}
        {currentStep === 'submitting' && (
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-tsvmap-orange mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Processing Your Results</h2>
            <p className="text-gray-600">Please wait while we analyze your responses...</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-sm text-gray-500">
        <p>© {new Date().getFullYear()} TSVMap. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Assessment;
